import { Edge, MarkerType, Node } from 'reactflow';
import { colors } from '@src/styles';
import { NODE_WIDTH } from '@src/components/nodes/BaseNode';

export interface BlockDictionary {
  [key: string]: { next: string[]; type?: string; label?: string };
}

const KNOWN_NODE_TYPES = new Set([
  'ACTION',
  'AUTH',
  'BRANCH',
  'CONDITION',
  'DEFAULT',
  'ERROR',
  'FLOW',
  'LOOP',
  'MICROSOFT',
  'RESPONSE',
  'TRIGGER',
  'VIOLATION',
]);

const resolveNodeType = (type?: string): string => {
  if (!type) return 'DEFAULT';
  if (type.startsWith('MICROSOFT')) return 'MICROSOFT';
  return KNOWN_NODE_TYPES.has(type) ? type : 'DEFAULT';
};

const HORIZONTAL_GAP = 60;
const LAYER_HEIGHT = 96;

export const calculateNodePositions = (input: BlockDictionary): { nodes: Node[]; edges: Edge[] } => {
  const blockDictionary: BlockDictionary = structuredClone(input);
  const nodes: Node[] = [];
  const edges: Edge[] = [];
  const correctOrder = new Set<string>();
  const blockLayers: Record<string, number> = {};
  const blocksForLayers: Record<string, { max: number; positioned: number }> = {};
  let maxBlocksByLayer = 0;

  if (!blockDictionary.startTrigger) return { nodes, edges };

  const maxLayers = Object.keys(blockDictionary).length + 1;
  let toExecuteArray = ['startTrigger'];
  let executionLayer = 0;

  while (toExecuteArray.length > 0 && executionLayer < maxLayers) {
    const newToExecuteArray: string[] = [];
    for (const blockName of toExecuteArray) {
      if (!blockDictionary[blockName]) {
        blockDictionary[blockName] = { next: [], type: 'ERROR' };
      }
      blockLayers[blockName] = executionLayer;
      if (correctOrder.has(blockName)) correctOrder.delete(blockName);
      correctOrder.add(blockName);
      for (const nextBlock of blockDictionary[blockName].next) {
        if (!newToExecuteArray.includes(nextBlock)) newToExecuteArray.push(nextBlock);
      }
    }
    blocksForLayers[executionLayer] = { max: 0, positioned: 0 };
    toExecuteArray = newToExecuteArray;
    executionLayer += 1;
  }

  Object.values(blockLayers).forEach((layer) => {
    blocksForLayers[layer].max += 1;
    maxBlocksByLayer = Math.max(maxBlocksByLayer, blocksForLayers[layer].max);
  });

  const slotWidth = NODE_WIDTH + HORIZONTAL_GAP;
  const maxWidth = maxBlocksByLayer * slotWidth - HORIZONTAL_GAP;

  for (const blockName of correctOrder.values()) {
    const blockData = blockDictionary[blockName];
    const layerNumber = blockLayers[blockName];
    const layerData = blocksForLayers[layerNumber];
    const spacing = (maxWidth - layerData.max * NODE_WIDTH) / (layerData.max + 1);

    nodes.push({
      id: blockName,
      type: resolveNodeType(blockData.type),
      data: { label: blockData.label ?? blockName, variant: blockData.type },
      position: {
        x: (spacing + NODE_WIDTH) * layerData.positioned + spacing,
        y: LAYER_HEIGHT * layerNumber,
      },
    });

    blockData.next.forEach((target) => {
      edges.push({
        id: `e${blockName}-${target}`,
        source: blockName,
        target,
        type: 'smoothstep',
        animated: true,
        style: { stroke: 'rgba(255, 255, 255, 0.28)', strokeWidth: 1.5 },
        markerEnd: { type: MarkerType.ArrowClosed, width: 14, height: 14, color: colors.brand.textMuted },
      });
    });
    layerData.positioned += 1;
  }

  return { nodes, edges };
};
